/**
 * Keep the existing admin/help pages inside the Marketer View shell (SYR-68).
 *
 * Those pages hardcode absolute `/dashboard/...` navigation. When they are
 * rendered inside the marketer shell (i.e. a `:brandHash` route param is
 * present) we rewrite those targets to the marketer-view equivalents so the
 * user never breaks out of the new view. At `/dashboard/*` (no brandHash) the
 * paths pass through unchanged.
 *
 * Marketer route map (mirrors App.tsx):
 *   /dashboard/surveys…        → /marketer-view/:hash/admin/surveys…
 *   /dashboard/customers…      → /marketer-view/:hash/admin/customers…
 *   /dashboard/organisation…   → /marketer-view/:hash/admin/organisation…
 *   /dashboard/support…        → /marketer-view/:hash/help…
 *   /dashboard/analytics/trends… → /marketer-view/:hash/benchmarks…  (Trends tab)
 *   /dashboard/benchmarking…   → /marketer-view/:hash/benchmarks…
 *   /dashboard/profile…        → /marketer-view/:hash/admin/profile…
 */

import { useCallback } from 'react';
import {
  useNavigate,
  useParams,
  useLocation,
  type NavigateOptions,
} from 'react-router-dom';
import { useMarketerRoot } from '../../hooks/useDashboardConfig';

/**
 * Pure mapper: dashboard path → marketer path, but ONLY when we are actually
 * rendering inside the Marketer View shell for `brandHash` (i.e. the current
 * pathname is under `${root}/${brandHash}`). Other routes that happen to carry
 * a `:brandHash` param (e.g. the admin analytics page) must NOT be remapped, so
 * `currentPath` is required to gate this — presence of a brandHash alone is not
 * enough. `root` is '/marketer-view' (flag off) or '/dashboard' (SYR-79 on).
 */
export function mapAdminPath(
  path: string,
  brandHash?: string,
  root: string = '/marketer-view',
  currentPath?: string,
): string {
  const base = `${root}/${brandHash}`;
  const inShell =
    !!brandHash &&
    !!currentPath &&
    (currentPath === base || currentPath.startsWith(`${base}/`));
  if (!inShell || typeof path !== 'string' || !path.startsWith('/dashboard/')) {
    return path;
  }
  const rest = path.slice('/dashboard'.length); // e.g. "/surveys/123?x=1"
  if (rest.startsWith('/support')) {
    return `${base}/help${rest.slice('/support'.length)}`;
  }
  if (rest.startsWith('/analytics/trends')) {
    return `${base}/benchmarks${rest.slice('/analytics/trends'.length)}`;
  }
  if (rest.startsWith('/benchmarking')) {
    return `${base}/benchmarks${rest.slice('/benchmarking'.length)}`;
  }
  if (rest.startsWith('/profile')) {
    return `${base}/admin/profile${rest.slice('/profile'.length)}`;
  }
  if (
    rest.startsWith('/surveys') ||
    rest.startsWith('/customers') ||
    rest.startsWith('/organisation')
  ) {
    return `${base}/admin${rest}`;
  }
  return path;
}

/** Returns a mapper bound to the current brand scope (for Link `to` / hrefs). */
export function useAdminPath(): (path: string) => string {
  const { brandHash } = useParams();
  const root = useMarketerRoot();
  const { pathname } = useLocation();
  return useCallback(
    (p: string) => mapAdminPath(p, brandHash, root, pathname),
    [brandHash, root, pathname],
  );
}

/**
 * Drop-in replacement for `useNavigate()` that keeps `/dashboard/*` targets
 * inside the marketer shell when embedded. Numeric (history) navigation and
 * non-dashboard paths pass straight through.
 */
export function useScopedNavigate(): (
  to: string | number,
  options?: NavigateOptions,
) => void {
  const navigate = useNavigate();
  const { brandHash } = useParams();
  const root = useMarketerRoot();
  const { pathname } = useLocation();
  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }
      navigate(mapAdminPath(to, brandHash, root, pathname), options);
    },
    [navigate, brandHash, root, pathname],
  );
}
